import Layout from "@components/common/Layout";
import React, { useMemo } from "react";
import WorldMapDark from "@assets/images/svg/world-map-dark.svg";
import CustomerAustralia from "@assets/images/svg/customer-australia.svg";
import CustomerNorthAmerica from "@assets/images/svg/customer-north-america.svg";
import CustomerAfrica from "@assets/images/svg/cutomer-africa.svg";
import CustomerSouthAmerica from "@assets/images/svg/cutomer-south-america.svg";
import CustomerEurope from "@assets/images/svg/customer-europe.svg";
import VizWrapper from "@components/common/Base/VizWrapper";
import { Variants, motion } from "framer-motion";
import { PageProps, graphql } from "gatsby";
import { AllSuccessStoriesQueryOneSuccessStoryModel } from "@models/success-story";
import useTagFilter from "@hooks/use-tag-filter";
import ImageRenderer from "@components/common/Image";
import Link from "@components/common/Link";
import { FiChevronRight } from "react-icons/fi";
import { TagFilterModel } from "@models/filter";
import Title from "@components/common/Title";

const wrapperVariant: Variants = {
  final: {
    transition: {
      staggerChildren: 0.5,
      delayChildren: 0.5,
    },
  },
};

const customerVariant: Variants = {
  initial: {
    opacity: 0,
  },
  final: {
    opacity: 1,
  },
};

export interface PageDataModel {
  allStrapiSuccessStory: {
    edges: AllSuccessStoriesQueryOneSuccessStoryModel[];
  };
  allStrapiCustomerSuccessTag: {
    edges: TagFilterModel[];
  };
}

const CustomerSuccessStories = (props: PageProps<PageDataModel>) => {
  const { allStrapiSuccessStory } = props?.data ?? {};
  const allStories = allStrapiSuccessStory?.edges ?? [];
  const { selectedFilters } = useTagFilter();

  const filteredData = useMemo(() => {
    const filteredWeb: AllSuccessStoriesQueryOneSuccessStoryModel[] = [];
    allStories.forEach((oneWeb) => {
      const { customer_success_tags: tags } = oneWeb?.node ?? {};
      const flatTagSlug = tags?.map((t) => t.slug);
      if (selectedFilters.some((f) => flatTagSlug?.includes(f))) {
        filteredWeb.push(oneWeb);
      }
    });
    return filteredWeb;
  }, [selectedFilters]);

  const renderOneSuccessStory = (
    story: AllSuccessStoriesQueryOneSuccessStoryModel
  ) => {
    const { title, slug, logo, statTitle, statSubtitle, statBgColor } =
      story?.node ?? {};
    const storyUrl = `/customers/${slug}`;
    return (
      <div className="w-[343px] sm:w-[200px] lg:w-[270px] p-[18px] shadow-lg rounded-2xl h-full flex flex-col justify-between">
        <div>
          <div className="h-[50px] flex items-center">
            {logo ? <ImageRenderer image={logo} alt={title} /> : <></>}
          </div>
          <div className="block text-lg lg:text-xl mt-8 mb-8 line-clamp-4 max-h-[112px] text-ellipsis">
            {title}
          </div>
        </div>
        <div className="flex flex-col w-[307px] sm:w-[164px] lg:w-[234px]">
          {statTitle || statSubtitle ? (
            <div
              style={{ backgroundColor: statBgColor ?? "#FFFFFF" }}
              className="p-3 rounded-lg mb-2"
            >
              {statTitle && (
                <Title variant="h3" titleText={statTitle} className="mb-0" />
              )}
              {statSubtitle && (
                <Title variant="paragraph" titleText={statSubtitle} />
              )}
            </div>
          ) : (
            <> </>
          )}
          <motion.div
            className="text-primary-blue-50 flex items-center mt-2"
            variants={{}}
            initial="initial"
            whileHover="hover"
          >
            <Link
              href={storyUrl}
              linkText="READ SUCCESS STORY"
              wrapperClassName="block text-sm "
              className="no-underline hover:font-semibold flex items-center text-primary-blue-50 font-semibold [&>svg]:hover:translate-x-2 capitalize"
              style={{
                color: "inherit",
                fontSize: "inherit",
                wordBreak: "break-word",
              }}
            />
            <motion.div
              className=""
              variants={{ initial: { x: 0 }, hover: { x: 4 } }}
            >
              <FiChevronRight className="inline-block ml-1 mb-1" size={16} />
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  };

  const renderStories = () => {
    if (selectedFilters?.length) {
      return filteredData?.map((oneBlogPreview) => {
        return (
          <div key={oneBlogPreview?.node?.id} className="flex justify-center">
            {renderOneSuccessStory(oneBlogPreview)}
          </div>
        );
      });
    } else {
      return allStories?.map((oneStory) => {
        return (
          <div key={oneStory?.node?.id} className="flex justify-center">
            {renderOneSuccessStory(oneStory)}
          </div>
        );
      });
    }
  };
  return (
    <Layout>
      <VizWrapper className="bg-dark-purple-50 py-[125px] md:py-[137] xl:py-[157px] px-4 md:px-8 lg:px-14 xl:px-[108px] flex flex-col lg:flex-row justify-between items-center">
        <div className="text-[40px] xl:text-[56px] font-bold xl:leading-[66px] leading-[48px] text-center lg:text-left mb-8">
          <div className="max-w-[611px]">
            <span className="text-white ">
              Trusted By
              <br />
            </span>
            <span className="text-violet-300">2500+ ECommerce </span>
            <span className="text-white">Leaders across borders</span>
          </div>
        </div>
        <motion.div
          className="relative hidden md:block"
          variants={wrapperVariant}
          initial="initial"
          animate="final"
        >
          <motion.div
            className="absolute bottom-[80px] right-4"
            variants={customerVariant}
            id="customer-australia"
          >
            <CustomerAustralia />
          </motion.div>
          <motion.div
            className="absolute top-[30px] left-[280px]"
            variants={customerVariant}
            id="customer-europe"
          >
            <CustomerEurope />
          </motion.div>
          <motion.div
            className="absolute top-[180px] left-[320px]"
            variants={customerVariant}
            id="customer-africa"
          >
            <CustomerAfrica />
          </motion.div>
          <motion.div
            className="absolute top-[30px] left-[50px]"
            variants={customerVariant}
            id="customer-north-america"
          >
            <CustomerNorthAmerica />
          </motion.div>
          <motion.div
            className="absolute top-[250px] left-[200px]"
            variants={customerVariant}
            id="customer-south-america"
          >
            <CustomerSouthAmerica />
          </motion.div>
          <motion.div id="world-map">
            <WorldMapDark />
          </motion.div>
        </motion.div>
      </VizWrapper>
      <div className="px-8 md:px-20 mt-20 lg:flex lg:gap-[89px]">
        {/* <div className="px-8 md:pl-20 md:pr-4 mt-20 lg:flex lg:gap-[89px]"> */}
        {/* <div className="shrink-0 mb-8">
          <TagFilter
            tags={allTags}
            isAllSelected={isAllSelected}
            selectedFilters={selectedFilters}
            handleAllSelection={handleAllSelection}
            handleFilterClick={handleFilterClick}
          />
        </div> */}
        <div className="mb-[67px] flex justify-center">
          {/* Uncomment below line when filter is added and delete the next line */}
          {/* <div className="flex justify-center sm:justify-start items-stretch flex-wrap gap-6"> */}
          <div className="flex justify-center items-stretch flex-wrap gap-6">
            {renderStories()}
          </div>
        </div>
      </div>
    </Layout>
  );
};
export default CustomerSuccessStories;
export const customerSuccessStoryQuery = graphql`
  query customerSuccess {
    allStrapiCustomerSuccessTag {
      edges {
        node {
          id
          title
          slug
        }
      }
    }
    allStrapiSuccessStory(sort: { createdAt: DESC }) {
      edges {
        node {
          id
          title
          slug
          statTitle
          statSubtitle
          statBgColor
          customer_success_tags {
            slug
          }
          logo {
            localFile {
              childImageSharp {
                gatsbyImageData(quality: 100, height: 50, placeholder: NONE)
              }
            }
          }
        }
      }
    }
  }
`;
